// PaymentRequests.jsx
import React, { useState, useEffect } from 'react';
import { CreditCard, Search, Inbox, Loader, Filter, BookOpen, Users, Award, TrendingUp, AlertTriangle, Eye } from 'lucide-react';
import * as d1Api from '../../services/d1Api';

const GRADE_LABELS = {
    '1sec': 'أولى ثانوي',
    '2sec': 'تانية ثانوي',
    '3sec': 'تالتة ثانوي',
};

const GRADE_COLORS = {
    '1sec': 'bg-sky-500/20 text-sky-300',
    '2sec': 'bg-amber-500/20 text-amber-300',
    '3sec': 'bg-violet-500/20 text-violet-300',
};

export const PaymentRequests = ({ onUpdate }) => {
    const [mainTab, setMainTab] = useState('payments'); // 'payments' or 'enrollments'
    
    // Payment Requests state
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [gradeFilter, setGradeFilter] = useState('');

    // Course Enrollments state
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [enrollments, setEnrollments] = useState([]);
    const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

    // Load courses on mount
    useEffect(() => {
        loadCourses();
    }, []);

    // Load payment requests when tab/filter changes
    useEffect(() => {
        if (mainTab === 'payments') {
            loadRequests();
        }
    }, [filter, mainTab]);

    // Load enrollments when selected course changes
    useEffect(() => {
        if (mainTab === 'enrollments' && selectedCourseId) {
            loadEnrollments();
        }
    }, [selectedCourseId, mainTab]);

    const loadCourses = async () => {
        const response = await d1Api.getCourses();
        if (response.success && response.data) {
            setCourses(response.data);
            if (response.data.length > 0 && !selectedCourseId) {
                setSelectedCourseId(response.data[0].id);
            }
        }
    };

    const loadRequests = async () => {
        setLoading(true);
        const response = await d1Api.getPaymentRequests(filter);
        if (response.success && response.data) {
            const formatted = response.data.map(req => ({
                id: req.id,
                userId: req.user_id,
                userName: [req.firstName, req.middleName, req.lastName].filter(Boolean).join(' ') || 'غير معروف',
                userPhone: req.phone || '',
                userGrade: req.grade || '',
                courseId: req.course_id,
                courseName: req.courseName || req.course_title || '',
                coursePrice: req.amount || 0,
                operationId: req.operationId || req.operation_id,
                status: req.status,
                createdAt: req.created_at ? new Date(Number(req.created_at) || req.created_at).getTime() : null
            }));
            setRequests(formatted);
        } else {
            setRequests([]);
        }
        setLoading(false);
    };

    const loadEnrollments = async () => {
        if (!selectedCourseId) return;
        setEnrollmentsLoading(true);
        try {
            const response = await d1Api.getEnrollmentsByCourse(selectedCourseId);
            if (response.success && response.data) {
                setEnrollments(response.data);
            } else {
                setEnrollments([]);
            }
        } catch (err) {
            console.error('Failed to load enrollments:', err);
            setEnrollments([]);
        }
        setEnrollmentsLoading(false);
    };

    const handleApprove = async (request) => {
        if (confirm(`تأكيد الموافقة على طلب ${request.userName}؟`)) {
            await d1Api.updatePaymentRequest(request.id, { status: 'approved' });
            loadRequests();
            if (onUpdate) onUpdate();
        }
    };

    const handleReject = async (requestId) => {
        if (confirm('تأكيد رفض الطلب؟')) {
            await d1Api.updatePaymentRequest(requestId, { status: 'rejected' });
            loadRequests();
            if (onUpdate) onUpdate();
        }
    };

    const handleRevokeEnrollment = async (enrollmentId) => {
        if (confirm('هل أنت متأكد من سحب هذا الاشتراك؟')) {
            const res = await d1Api.revokeEnrollment(enrollmentId);
            if (res.success) {
                setEnrollments(prev => prev.filter(e => e.enrollment_id !== enrollmentId));
                if (onUpdate) onUpdate();
            } else {
                alert('حدث خطأ: ' + (res.error || 'لم يتم السحب'));
            }
        }
    };

    const filtered = requests.filter(r => {
        const matchesGrade = gradeFilter ? r.userGrade === gradeFilter : true;
        const matchesSearch = searchQuery.trim()
            ? r.userName.includes(searchQuery) ||
              r.userPhone.includes(searchQuery) ||
              (r.operationId || '').includes(searchQuery)
            : true;
        return matchesGrade && matchesSearch;
    });

    const selectedCourse = courses.find(c => c.id === selectedCourseId);

    return (
        <div className="space-y-6 animate-slide-up">
            {/* Main Tabs */}
            <div className="flex gap-3">
                <button
                    onClick={() => setMainTab('payments')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition ${
                        mainTab === 'payments'
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                >
                    <CreditCard size={20} />
                    طلبات الدفع
                </button>
                <button
                    onClick={() => setMainTab('enrollments')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition ${
                        mainTab === 'enrollments'
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                >
                    <Users size={20} />
                    الطلاب المشتركين بالكورسات
                </button>
            </div>

            {/* ══════════════ PAYMENT REQUESTS TAB ══════════════ */}
            {mainTab === 'payments' && (
                <>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <h2 className="text-2xl font-bold flex items-center gap-2">
                            <CreditCard size={24} /> طلبات الدفع
                        </h2>

                        <div className="flex gap-2">
                            {['pending', 'approved', 'rejected'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setFilter(status)}
                                    className={`px-4 py-2 rounded-xl font-bold transition ${filter === status
                                            ? 'bg-brand-primary text-white'
                                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                                        }`}
                                >
                                    {status === 'pending' && 'قيد الانتظار'}
                                    {status === 'approved' && 'تمت الموافقة'}
                                    {status === 'rejected' && 'مرفوضة'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search + Grade Filter */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="text"
                                placeholder="ابحث باسم الطالب أو رقم الهاتف أو رقم العملية..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pr-12 pl-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-brand-gold outline-none"
                            />
                        </div>
                        <div className="relative">
                            <Filter size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                            <select
                                value={gradeFilter}
                                onChange={(e) => setGradeFilter(e.target.value)}
                                className="pr-10 pl-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white appearance-none cursor-pointer focus:border-brand-gold outline-none min-w-[160px]"
                            >
                                <option value="" style={{ background: '#1a1a2e' }}>كل الصفوف</option>
                                <option value="1sec" style={{ background: '#1a1a2e' }}>أولى ثانوي</option>
                                <option value="2sec" style={{ background: '#1a1a2e' }}>تانية ثانوي</option>
                                <option value="3sec" style={{ background: '#1a1a2e' }}>تالتة ثانوي</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <Loader size={32} className="mx-auto mb-4 animate-spin text-slate-400" />
                            <p className="text-slate-400">جاري التحميل...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="glass-panel p-12 text-center">
                            <Inbox size={48} className="mx-auto mb-4 opacity-50" />
                            <h3 className="text-xl font-bold text-slate-400">لا توجد طلبات</h3>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filtered.map((request) => (
                                <div key={request.id} className="glass-panel p-6">
                                    <div className="flex flex-col md:flex-row justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-brand-primary/20 flex items-center justify-center">
                                                <span className="text-xl">💰</span>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="font-bold text-lg">{request.userName}</h4>
                                                    {request.userGrade && (
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${GRADE_COLORS[request.userGrade] || 'bg-white/10 text-slate-300'}`}>
                                                            {GRADE_LABELS[request.userGrade] || request.userGrade}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-400">{request.userPhone}</p>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded text-xs">
                                                        {request.courseName || request.courseId}
                                                    </span>
                                                    <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded text-xs">
                                                        {request.coursePrice} ج.م
                                                    </span>
                                                    <span className="bg-white/10 px-2 py-1 rounded text-xs">
                                                        {request.createdAt ? new Date(request.createdAt).toLocaleDateString('ar-EG') : 'تاريخ غير معروف'}
                                                    </span>
                                                </div>

                                                {request.operationId && (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-bold mb-1">رقم العملية (موجود في رسالة الواتساب للطالب):</p>
                                                        <p className="text-sm font-mono text-brand-accent bg-black/40 px-2 py-1 rounded inline-block">
                                                            {request.operationId}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {filter === 'pending' && (
                                            <div className="flex gap-2 self-start">
                                                <button
                                                    onClick={() => handleApprove(request)}
                                                    className="bg-green-500/20 hover:bg-green-500 text-green-300 hover:text-white px-6 py-2 rounded-xl font-bold transition"
                                                >
                                                    موافقة
                                                </button>
                                                <button
                                                    onClick={() => handleReject(request.id)}
                                                    className="bg-red-500/20 hover:bg-red-500 text-red-300 hover:text-white px-6 py-2 rounded-xl font-bold transition"
                                                >
                                                    رفض
                                                </button>
                                            </div>
                                        )}

                                        {filter === 'approved' && (
                                            <span className="bg-green-500/20 text-green-300 px-4 py-2 rounded-xl font-bold self-start">
                                                تمت الموافقة ✓
                                            </span>
                                        )}

                                        {filter === 'rejected' && (
                                            <span className="bg-red-500/20 text-red-300 px-4 py-2 rounded-xl font-bold self-start">
                                                مرفوض ✗
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ══════════════ COURSE ENROLLMENTS TAB ══════════════ */}
            {mainTab === 'enrollments' && (
                <>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <BookOpen size={24} /> الطلاب المشتركين بالكورسات
                    </h2>

                    {/* Course Tabs - Horizontal Scrollable */}
                    <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
                        {courses.map((course) => (
                            <button
                                key={course.id}
                                onClick={() => setSelectedCourseId(course.id)}
                                className={`px-5 py-3 rounded-xl font-bold whitespace-nowrap transition-all duration-200 ${
                                    selectedCourseId === course.id
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                                        : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'
                                }`}
                            >
                                {course.title}
                            </button>
                        ))}
                    </div>

                    {/* Follow-up Statistics */}
                    {selectedCourse && enrollments.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            {(() => {
                                const excellent = enrollments.filter(s => {
                                    const pct = s.total_lectures > 0 ? (s.completed_lectures / s.total_lectures) * 100 : 0;
                                    return pct > 85;
                                }).length;
                                const good = enrollments.filter(s => {
                                    const pct = s.total_lectures > 0 ? (s.completed_lectures / s.total_lectures) * 100 : 0;
                                    return pct >= 50 && pct <= 85;
                                }).length;
                                const needsFollow = enrollments.filter(s => {
                                    const pct = s.total_lectures > 0 ? (s.completed_lectures / s.total_lectures) * 100 : 0;
                                    return pct < 50;
                                }).length;
                                return (
                                    <>
                                        <div className="glass-panel p-5 border-b-4 border-green-500 text-center relative overflow-hidden">
                                            <div className="absolute -right-4 -top-4 opacity-10"><Award size={80} className="text-green-500" /></div>
                                            <p className="text-slate-400 font-bold mb-2">ممتاز 🌟</p>
                                            <p className="text-4xl font-black text-green-400">{excellent}</p>
                                            <p className="text-xs text-slate-500 mt-2">طلاب بتقدم فوق 85%</p>
                                        </div>
                                        <div className="glass-panel p-5 border-b-4 border-yellow-500 text-center relative overflow-hidden">
                                            <div className="absolute -right-4 -top-4 opacity-10"><TrendingUp size={80} className="text-yellow-500" /></div>
                                            <p className="text-slate-400 font-bold mb-2">جيد 👍</p>
                                            <p className="text-4xl font-black text-yellow-400">{good}</p>
                                            <p className="text-xs text-slate-500 mt-2">طلاب بين 50% - 85%</p>
                                        </div>
                                        <div className="glass-panel p-5 border-b-4 border-red-500 text-center relative overflow-hidden">
                                            <div className="absolute -right-4 -top-4 opacity-10"><AlertTriangle size={80} className="text-red-500" /></div>
                                            <p className="text-slate-400 font-bold mb-2">يحتاج متابعة ⚠️</p>
                                            <p className="text-4xl font-black text-red-400">{needsFollow}</p>
                                            <p className="text-xs text-slate-500 mt-2">طلاب تحت 50%</p>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Enrollments Table */}
                    {selectedCourse && (
                        <div className="glass-panel p-6">
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Users size={20} />
                                الطلاب المشتركين في: {selectedCourse.title}
                                <span className="text-sm text-slate-400">({enrollments.length} طالب)</span>
                            </h3>

                            {enrollmentsLoading ? (
                                <div className="text-center py-8">
                                    <Loader size={32} className="mx-auto mb-4 animate-spin text-slate-400" />
                                    <p className="text-slate-400">جاري التحميل...</p>
                                </div>
                            ) : enrollments.length === 0 ? (
                                <div className="text-center py-8 text-slate-400">
                                    <Inbox size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>لا يوجد طلاب مشتركين في هذا الكورس</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-right">
                                        <thead className="border-b border-white/10">
                                            <tr>
                                                <th className="pb-3 text-slate-400">الاسم</th>
                                                <th className="pb-3 text-slate-400">رقم الهاتف</th>
                                                <th className="pb-3 text-slate-400">الصف الدراسي</th>
                                                <th className="pb-3 text-slate-400">المحاضرات</th>
                                                <th className="pb-3 text-slate-400">مستوى المتابعة</th>
                                                <th className="pb-3 text-slate-400">تاريخ الاشتراك</th>
                                                <th className="pb-3 text-slate-400">تاريخ الانتهاء</th>
                                                <th className="pb-3 text-slate-400 text-center">إجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {enrollments.map((student) => {
                                                const totalLectures = Number(student.total_lectures) || 0;
                                                const completedLectures = Number(student.completed_lectures) || 0;
                                                const avgScore = student.avg_score ? Math.round(Number(student.avg_score)) : null;
                                                const progressPct = totalLectures > 0 ? Math.round((completedLectures / totalLectures) * 100) : 0;
                                                
                                                let badgeColor = 'bg-red-500/20 text-red-400';
                                                let badgeLabel = 'مقصر';
                                                let levelLabel = 'C';
                                                if (progressPct > 80) {
                                                    badgeColor = 'bg-green-500/20 text-green-400';
                                                    badgeLabel = 'ممتاز';
                                                    levelLabel = 'A';
                                                } else if (progressPct > 40) {
                                                    badgeColor = 'bg-yellow-500/20 text-yellow-400';
                                                    badgeLabel = 'متابع';
                                                    levelLabel = 'B';
                                                }

                                                const handleShowDetails = () => {
                                                    const scoreText = avgScore !== null ? `${avgScore}%` : 'لم يحل امتحانات بعد';
                                                    alert(`الطالب: ${[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}\nأكمل ${completedLectures} من أصل ${totalLectures} محاضرة (${progressPct}%)\nمتوسط درجاته في الامتحانات: ${scoreText}`);
                                                };

                                                return (
                                                    <tr key={student.enrollment_id} className="border-b border-white/5 hover:bg-white/5 transition">
                                                        <td className="py-4 font-bold">
                                                            {[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}
                                                        </td>
                                                        <td className="py-4">{student.phone || '-'}</td>
                                                        <td className="py-4">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${GRADE_COLORS[student.grade] || 'bg-white/10 text-slate-300'}`}>
                                                                {GRADE_LABELS[student.grade] || student.grade || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                                                                    <div
                                                                        className={`h-full rounded-full ${progressPct > 80 ? 'bg-green-500' : progressPct > 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                                        style={{ width: `${progressPct}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-xs text-slate-400">{completedLectures}/{totalLectures}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 text-center">
                                                            <div className="flex flex-col items-center gap-1">
                                                                <span className={`px-3 py-1 rounded-lg font-bold text-xs ${badgeColor}`}>
                                                                    {badgeLabel}
                                                                </span>
                                                                <button
                                                                    onClick={handleShowDetails}
                                                                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                                                >
                                                                    <Eye size={12} />
                                                                    مستوى {levelLabel}
                                                                    {avgScore !== null && ` (${avgScore}%)`}
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 text-slate-300">
                                                            {student.enrolled_at ? new Date(Number(student.enrolled_at)).toLocaleDateString('ar-EG') : '-'}
                                                        </td>
                                                        <td className="py-4 text-slate-300">
                                                            {student.expires_at ? new Date(Number(student.expires_at)).toLocaleDateString('ar-EG') : '-'}
                                                        </td>
                                                        <td className="py-4 text-center">
                                                            <button
                                                                onClick={() => handleRevokeEnrollment(student.enrollment_id)}
                                                                className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 rounded-lg text-xs font-bold transition"
                                                            >
                                                                سحب الاشتراك
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};